import type { ExerciseLong, ExerciseMcq, ExerciseShort } from "~/features/computer/data/exercises/exercise.types";
import { algorithmExercises } from "~/features/computer/data/exercises/algorithm.exercises";
import { dataAnalyticsExercises } from "~/features/computer/data/da/da.exercises";
import { dsExercises } from "~/features/computer/data/ds/ds.exercises";
import { computerMcqChapterByUnit } from "~/features/computer/data/mcq-registry";
import { sdlcExercises } from "~/features/computer/data/sdlc/sdlc.exercises";

export type PdfChapterLink = {
  label: string;
  to: string;
};

export type PdfNotesChapter = {
  slug: string;
  chapterNumber: number;
  chapterLabel: string;
  title: string;
  pageTitle: string;
  description: string;
  keywords: string;
  intro: string;
  overview: string;
  fullNotesIntro: string;
  solvedExercisesIntro: string;
  importantQuestionsIntro: string;
  noteBlocks: Array<{
    title: string;
    body: string;
  }>;
  keyPoints: string[];
  conceptCards?: Array<{
    title: string;
    detail: string;
  }>;
  mcqs: ExerciseMcq[];
  shortQuestions: ExerciseShort[];
  longAnswers: ExerciseLong[];
  links: PdfChapterLink[];
};

const pythonShortQuestions: ExerciseShort[] = [
  {
    id: "python-pdf-short-1",
    question: "Variable aur value me basic farq kya hai?",
    answer: "Variable naam hota hai aur value wo actual data hota hai jo us naam ke andar store kiya jata hai.",
  },
  {
    id: "python-pdf-short-2",
    question: "Loop exam point of view se kyun important hai?",
    answer: "Loop repetition ka concept clear karta hai. Board questions me for loop aur while loop ka basic difference aksar poocha jata hai.",
  },
  {
    id: "python-pdf-short-3",
    question: "Function ke sath return ka role kya hai?",
    answer: "Return function ke result ko wapas bhejta hai taake us output ko program ke doosre part me use kiya ja sake.",
  },
];

const pythonLongAnswers: ExerciseLong[] = [
  {
    id: "python-pdf-long-1",
    question: "Python ke core topics ko chapter flow me explain karo.",
    solution: "Python chapter variables se start hota hai, phir input-output, operators, conditions, loops aur functions ki taraf move karta hai. Yeh sequence is liye important hai kyun ke har naya topic pichlay concept par build hota hai.",
    steps: [
      "Variables se data store karna samjho.",
      "input() aur print() se user interaction samjho.",
      "Operators se calculations aur comparisons karo.",
      "If-else se decisions lo.",
      "Loops se repetition handle karo.",
      "Functions se reusable code likho.",
    ],
  },
  {
    id: "python-pdf-long-2",
    question: "For loop aur while loop me difference explain karo.",
    solution: "For loop tab use hota hai jab repeat count ya iterable clear ho. While loop tab useful hota hai jab condition ke true rehne tak process repeat karna ho. Dono repetition karte hain lekin control style alag hoti hai.",
    steps: [
      "For loop known sequence ya range par chalti hai.",
      "While loop condition-based repetition karti hai.",
      "While me condition update na ho to infinite loop aa sakta hai.",
    ],
  },
];

export const pdfNotesChapters: PdfNotesChapter[] = [
  {
    slug: "sdlc",
    chapterNumber: 1,
    chapterLabel: "Chapter 1",
    title: "Software Development Life Cycle",
    pageTitle: "Chapter 1 Computer Notes PDF (Solved Exercises) - Class 11",
    description:
      "Chapter 1 computer notes PDF style page for class 11 with SDLC full notes, solved exercises, important questions and print-friendly download flow.",
    keywords:
      "class 11 computer notes pdf, chapter 1 computer notes pdf, sdlc solved exercises pdf, 11th class computer chapter 1 notes",
    intro:
      "Chapter 1 SDLC notes ko PDF-style layout me rakha gaya hai taake user ko quick access, printable format aur low-friction revision mile. Neeche full notes, solved exercises aur important questions aik hi page par available hain.",
    overview:
      "Chapter 1 ka focus Software Development Life Cycle par hai. Is chapter me software banane ke main stages, requirement types, planning mindset aur common development models ko samjhaya jata hai.",
    fullNotesIntro:
      "Neeche diye gaye notes exam preparation ke liye concise hain, lekin itne complete hain ke student chapter ka overall structure samajh sake aur short ya long answer me points build kar sake.",
    solvedExercisesIntro:
      "Solved exercises ka purpose direct exam wording ko manageable answers me convert karna hai. In examples ko yaad karne ke bajaye inka answer structure samajhna zyada important hai.",
    importantQuestionsIntro:
      "Yeh section board-style short questions ke liye useful hai. In ko revise karke student concept wording ko fast recall me convert kar sakta hai.",
    noteBlocks: [
      {
        title: "SDLC kya hai?",
        body:
          "Software Development Life Cycle aik structured process hai jo software project ko planning se deployment tak step-by-step chalata hai. Is se team ko clear direction milti hai aur project random coding ke bajaye organized tareeqe se complete hota hai.",
      },
      {
        title: "SDLC kyun important hai?",
        body:
          "Is ka sab se bara faida control hai. Jab requirements clear hoti hain, design pehle socha jata hai aur testing later ignore nahi hoti, to bugs, delays aur unnecessary cost dono kam ho jate hain.",
      },
      {
        title: "Requirements ka role",
        body:
          "Functional requirements yeh batati hain ke system kya kaam karega, jaise login ya search. Non-functional requirements system ki quality define karti hain, jaise speed, reliability, security aur performance.",
      },
    ],
    keyPoints: [
      "Planning: scope, budget, timeline aur risk ko define karo.",
      "Requirement Analysis: user needs aur system behavior ko clear karo.",
      "Design: structure, screens, database aur workflow decide karo.",
      "Development: approved design ke mutabiq coding karo.",
      "Testing: bugs, errors aur weak flows detect karo.",
      "Deployment: final software users ke liye release karo.",
    ],
    conceptCards: [
      {
        title: "Waterfall Model",
        detail:
          "Linear model hai jahan har stage fixed order me chalti hai. Stable requirements wale projects me easy hota hai lekin late changes handle karna mushkil hota hai.",
      },
      {
        title: "Agile Model",
        detail:
          "Project ko small cycles ya sprints me divide karta hai. Feedback jaldi milta hai aur improvement continuously hoti rehti hai, is liye changing requirements ke liye useful hota hai.",
      },
    ],
    mcqs: sdlcExercises.mcqs,
    shortQuestions: sdlcExercises.short,
    longAnswers: sdlcExercises.long,
    links: [
      { label: "Notes", to: "/11th/computer/sdlc/" },
      { label: "MCQs", to: "/11th/computer/sdlc/mcqs/" },
      { label: "Short Questions", to: "/11th/computer/sdlc/short-questions/" },
      { label: "Exercise", to: "/11th/computer/sdlc/exercise/" },
    ],
  },
  {
    slug: "python",
    chapterNumber: 2,
    chapterLabel: "Chapter 2",
    title: "Python Basics",
    pageTitle: "Chapter 2 Python Notes PDF (Solved Exercises) - Class 11",
    description:
      "Chapter 2 Python notes PDF style page for class 11 with variables, loops, functions, solved exercises and printable revision layout.",
    keywords:
      "class 11 python notes pdf, chapter 2 python solved exercises pdf, 11th class python notes printable",
    intro:
      "Chapter 2 Python notes ko document-style layout me organize kiya gaya hai taake student notes, solved structure aur important questions ko aik hi printable page par dekh sake.",
    overview:
      "Python chapter programming ki basic language concepts introduce karta hai. Is me variables, input-output, operators, conditions, loops aur functions se coding ka foundation build hota hai.",
    fullNotesIntro:
      "Yeh notes concept flow ke mutabiq arranged hain. Agar student sequence follow kare to chapter ratne ke bajaye samajh kar revise kar sakta hai.",
    solvedExercisesIntro:
      "Python solved section ka focus answer structure aur concept linkage par hai. Is se descriptive aur objective dono prep ko support milta hai.",
    importantQuestionsIntro:
      "Important questions chapter ke high-frequency topics ko cover karte hain. In ko practice karne se short-answer grip strong hoti hai.",
    noteBlocks: [
      {
        title: "Variables aur data storage",
        body:
          "Python me variable aik named storage hota hai jahan value temporarily rakhi jati hai. Yeh programming ka base concept hai kyun ke baqi calculations aur logic isi stored data par kaam karte hain.",
      },
      {
        title: "Input aur output",
        body:
          "input() user se value leta hai aur print() result dikhata hai. Dono mil kar program ko interactive banate hain aur basic programs me sab se zyada use hote hain.",
      },
      {
        title: "Decision aur repetition",
        body:
          "If-else conditions ke zariye decision leta hai, jab ke loops repeated tasks automate karte hain. Yeh dono concepts paper me practical coding logic samjhane ke liye important hote hain.",
      },
      {
        title: "Functions ka faida",
        body:
          "Function reusable code block hota hai. Is se same logic dobara likhne ki zarurat kam hoti hai aur code ko organize karna easy ho jata hai.",
      },
    ],
    keyPoints: [
      "Variable naam aur value ka relation clear rakho.",
      "input() value leta hai, print() output dikhata hai.",
      "Operators calculations aur comparisons dono me use hote hain.",
      "If-else decision making ka base hai.",
      "For aur while loops repetition ko control karte hain.",
      "Functions code ko modular aur reusable banate hain.",
    ],
    conceptCards: [
      {
        title: "For Loop",
        detail:
          "Known range ya iterable par run hoti hai. Jab repeat count clear ho to for loop zyada readable hoti hai.",
      },
      {
        title: "While Loop",
        detail:
          "Condition ke true rehne tak chalti hai. Agar update miss ho jaye to infinite loop ban sakta hai, is liye logic clear hona chahiye.",
      },
    ],
    mcqs: computerMcqChapterByUnit.python.questions.slice(0, 4),
    shortQuestions: pythonShortQuestions,
    longAnswers: pythonLongAnswers,
    links: [
      { label: "Notes", to: "/11th/computer/python/notes/" },
      { label: "MCQs", to: "/11th/computer/python/mcqs/" },
      { label: "Short Questions", to: "/11th/computer/python/short-questions/" },
      { label: "Solved Exercise", to: "/11th/computer/python/solved-exercise/" },
    ],
  },
  {
    slug: "algorithm",
    chapterNumber: 3,
    chapterLabel: "Chapter 3",
    title: "Algorithms and Problem Solving",
    pageTitle: "Chapter 3 Algorithm Notes PDF (Solved Exercises) - Class 11",
    description:
      "Chapter 3 algorithm notes PDF style page for class 11 with problem solving, sorting, searching and printable solved revision blocks.",
    keywords:
      "class 11 algorithm notes pdf, chapter 3 algorithm solved exercises pdf, 11th class computer algorithm notes",
    intro:
      "Chapter 3 ko PDF-style study page me convert kiya gaya hai taake problem solving, searching aur sorting ko compact printable revision form me padha ja sake.",
    overview:
      "Algorithm chapter problem ko structured steps me solve karna sikhata hai. Is me well-defined problems, efficiency, searching aur sorting ka basic framework hota hai.",
    fullNotesIntro:
      "Full notes ka target yeh hai ke student algorithm ko sirf definition tak limit na kare balki us ke practical flow ko bhi samjhe.",
    solvedExercisesIntro:
      "Solved exercises se answer framing easy hoti hai. Algorithm chapter me direct steps aur comparison points likhna bohat important hota hai.",
    importantQuestionsIntro:
      "Important questions commonly poochay jane wale definitions aur process-based prompts ko cover karte hain.",
    noteBlocks: [
      {
        title: "Algorithm ka basic idea",
        body:
          "Algorithm step-by-step instructions ka ordered set hota hai jo kisi problem ko solve karta hai. Is ka core focus clarity aur sequence par hota hai.",
      },
      {
        title: "Problem solving mindset",
        body:
          "Pehle problem ko samjha jata hai, phir input-output define kiye jate hain, phir process ko small steps me divide karke solution build hota hai.",
      },
      {
        title: "Searching aur sorting",
        body:
          "Linear search ek ek element check karti hai, jab ke sorting techniques data ko order me arrange karti hain. Bubble sort jaise methods exam me process explanation ke liye common hain.",
      },
    ],
    keyPoints: [
      "Well-defined problem me input, process aur output clear hotay hain.",
      "Algorithm random kaam nahi karta, ordered steps follow karta hai.",
      "Efficiency ka simple idea kam steps me better result hai.",
      "Linear search sequential checking karta hai.",
      "Bubble sort adjacent values compare aur swap karta hai.",
    ],
    conceptCards: [
      {
        title: "Linear Search",
        detail:
          "Simple aur easy method hai jo list ko start se end tak check karta hai. Small data sets me conceptually bohat clear hota hai.",
      },
      {
        title: "Bubble Sort",
        detail:
          "Adjacent values compare hoti hain aur wrong order me swap hota hai. Repeated passes ke baad list sorted ho jati hai.",
      },
    ],
    mcqs: algorithmExercises.mcqs,
    shortQuestions: algorithmExercises.short,
    longAnswers: algorithmExercises.long,
    links: [
      { label: "Overview", to: "/11th/computer/algorithm/" },
      { label: "MCQs", to: "/11th/computer/algorithm/mcqs/" },
      { label: "Short Questions", to: "/11th/computer/algorithm/short-questions/" },
      { label: "Exercise", to: "/11th/computer/algorithm/exercise/" },
    ],
  },
  {
    slug: "data-structures",
    chapterNumber: 4,
    chapterLabel: "Chapter 4",
    title: "Data Structures",
    pageTitle: "Chapter 4 Data Structures Notes PDF (Solved Exercises) - Class 11",
    description:
      "Chapter 4 data structures notes PDF style page for class 11 with lists, stacks, queues, trees, graphs and printable revision blocks.",
    keywords:
      "class 11 data structures notes pdf, chapter 4 solved exercises pdf, 11th class computer data structures notes",
    intro:
      "Chapter 4 ko document-style printable page me arrange kiya gaya hai taake lists, stacks, queues aur graphs ko board prep ke liye aik hi flow me revise kiya ja sake.",
    overview:
      "Data Structures chapter data ko organize aur access karne ke basic models samjhata hai. Lists, stacks, queues, trees aur graphs is chapter ke main anchors hain.",
    fullNotesIntro:
      "Yeh notes chapter ke concepts ko definition se zyada practical angle se explain karte hain taake student operations aur examples dono samajh sake.",
    solvedExercisesIntro:
      "Solved exercise blocks se operation names aur descriptive answers ko yaad rakhna easy hota hai. Data structures me real-life examples strong marks dilate hain.",
    importantQuestionsIntro:
      "Important questions chapter ke core structures aur un ke operations ko target karte hain. In se short-answer base strong hoti hai.",
    noteBlocks: [
      {
        title: "Lists ka role",
        body:
          "List flexible data structure hai jisme multiple values aik jagah store ki jati hain. Python context me list dynamic hoti hai aur append, insert, remove jaise operations support karti hai.",
      },
      {
        title: "Stack aur queue",
        body:
          "Stack LIFO principle follow karta hai jab ke queue FIFO. Dono structures operations ke through real-life examples ke sath asani se samjhay ja sakte hain.",
      },
      {
        title: "Tree aur graph",
        body:
          "Tree hierarchical relations dikhata hai aur graph connected nodes ka network represent karta hai. Yeh dono chapter ke higher-level concepts hain lekin basic definition aur properties paper me enough hoti hain.",
      },
    ],
    keyPoints: [
      "List dynamic size support karti hai.",
      "append() end me item add karta hai.",
      "Stack LIFO aur queue FIFO principle follow karte hain.",
      "Enqueue add aur dequeue remove operation hota hai.",
      "Tree hierarchical structure hai.",
      "Graph nodes aur edges ka network hota hai.",
    ],
    conceptCards: [
      {
        title: "Stack",
        detail:
          "Plates ki stack is ka simple example hai. Last rakhi hui plate sab se pehle nikalti hai, isi ko LIFO kehte hain.",
      },
      {
        title: "Queue",
        detail:
          "Line me kharay log queue ka basic example hain. Pehla banda pehle jata hai, isi ko FIFO bola jata hai.",
      },
    ],
    mcqs: dsExercises.mcqs,
    shortQuestions: dsExercises.short,
    longAnswers: dsExercises.long,
    links: [
      { label: "Overview", to: "/11th/computer/data-structures/" },
      { label: "MCQs", to: "/11th/computer/data-structures/mcqs/" },
      { label: "Short Questions", to: "/11th/computer/data-structures/short-questions/" },
      { label: "Exercise", to: "/11th/computer/data-structures/exercise/" },
    ],
  },
  {
    slug: "data-analytics",
    chapterNumber: 5,
    chapterLabel: "Chapter 5",
    title: "Data Analytics",
    pageTitle: "Chapter 5 Data Analytics Notes PDF (Solved Exercises) - Class 11",
    description:
      "Chapter 5 data analytics notes PDF style page for class 11 with visualization, models, experimental design and printable revision support.",
    keywords:
      "class 11 data analytics notes pdf, chapter 5 solved exercises pdf, 11th class computer data analytics notes",
    intro:
      "Chapter 5 ko PDF-intent revision page me convert kiya gaya hai taake charts, models aur experimentation ko quick printable format me padha ja sake.",
    overview:
      "Data Analytics chapter ka focus raw data ko samajhne, patterns identify karne aur simple models ya visualization ke zariye insights nikalne par hota hai.",
    fullNotesIntro:
      "Full notes me heavy math avoid karke concept clarity par focus rakha gaya hai. Board level par terminology aur use case samajhna zyada important hota hai.",
    solvedExercisesIntro:
      "Solved blocks chapter ki abstract cheezon ko concrete examples me convert karte hain. Is se long answers aur MCQs dono easy ho jate hain.",
    importantQuestionsIntro:
      "Important questions visualization, models aur experimentation ke core ideas ko target karte hain. In ko revise karke chapter ki language pakri ja sakti hai.",
    noteBlocks: [
      {
        title: "Data analytics kya karti hai?",
        body:
          "Data analytics raw numbers ko useful information me convert karti hai. Is ka purpose patterns dekhna, decisions support karna aur trends ko samajhna hota hai.",
      },
      {
        title: "Visualization ka role",
        body:
          "Charts aur graphs data ko readable banate hain. Jab raw table ko visual form me dikhaya jata hai to trend aur comparison jaldi samajh aate hain.",
      },
      {
        title: "Models aur experiments",
        body:
          "Simple models jaise linear regression prediction ka basic idea dete hain, jab ke experimental design hypotheses ko test karne me help karti hai.",
      },
    ],
    keyPoints: [
      "Data analytics ka focus patterns aur insights par hota hai.",
      "Visualization trends ko clear banati hai.",
      "Linear regression simple prediction model hai.",
      "Experimental design comparison aur testing par based hoti hai.",
      "K-means similar data ko groups me divide karta hai.",
    ],
    conceptCards: [
      {
        title: "Bar Chart vs Line Chart",
        detail:
          "Bar chart categories compare karta hai, jab ke line chart time ke sath trend dikhane me zyada useful hota hai.",
      },
      {
        title: "Linear Regression",
        detail:
          "Trend-based simple model hai jo existing data dekh kar next value ka estimate lagata hai.",
      },
    ],
    mcqs: dataAnalyticsExercises.mcqs,
    shortQuestions: dataAnalyticsExercises.short,
    longAnswers: dataAnalyticsExercises.long,
    links: [
      { label: "Overview", to: "/11th/computer/data-analytics/" },
      { label: "MCQs", to: "/11th/computer/data-analytics/mcqs/" },
      { label: "Short Questions", to: "/11th/computer/data-analytics/short-questions/" },
      { label: "Exercise", to: "/11th/computer/data-analytics/exercise/" },
    ],
  },
];

export const pdfNotesChapterMap = Object.fromEntries(
  pdfNotesChapters.map((chapter) => [chapter.slug, chapter]),
) as Record<string, PdfNotesChapter>;
